import { defineStore } from 'pinia'
import CrmService from '@/services/crm/CrmService'
import { useContactsStore } from './contactsStore'
import { devLog, devError } from '@/services/logger'

const normalizeText = (value: string) =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')

interface ClosureReason {
  id: number
  name: string
  reason_type?: 'won' | 'lost' | 'invalid'
  description?: string
  order?: number
  system?: boolean
  is_active?: boolean
}

// Tipos para melhor tipagem
interface Lead {
  id: number
  name: string
  email: string
  phone: string
  company: number
  pipeline_stage: number
  current_stage?: {
    id: number
    name: string
    order: number
    pipeline: number
    is_active: boolean
  }
  assigned_to?: number
  assigned_to_details?: {
    id: number
    user_details: {
      id: number
      first_name: string
      last_name: string
      username: string
      fullname: string
    }
    company: number
    role: string
    is_active: boolean
    slug: string
  }
  temperature: string
  source: string
  vehicle?: number
  vehicle_details?: any
  notes: string
  created_at: string
  updated_at: string
  unreadMessages: number
  status?: string
  last_communication?: any
  activities?: any[]
  external_ticket?: any[]
  last_change_status?: any
  closed_at?: string | null
  closure_reason_id?: number | null
  closure_reason?: ClosureReason | null
  closure_notes?: string | null
  closed_by?: number | null
  closed_by_details?: any
}

interface Communication {
  id: number
  lead: number
  user: number
  user_details?: any
  channel: string
  content: string
  direction: 'incoming' | 'outgoing'
  created_at: string
  external_created_at?: string
  attachments?: any[]
}

interface PipelineStage {
  id: number
  pipeline: number
  name: string
  order: number
  is_active: boolean
  is_closed?: boolean
}

interface CompanyUser {
  id: number
  user: number
  user_details: {
    id: number
    first_name: string
    last_name: string
    username: string
    fullname: string
  }
  company: number
  role: string
  is_active: boolean
  slug: string
}

interface LeadsState {
  leads: Lead[]
  selectedLead: Lead | null
  communications: Communication[]
  pipelineStages: PipelineStage[]
  companyUsers: CompanyUser[]
  temperatureChoices: [string, string][]
  leadCounts: {
    pending_count: number
    scheduled_count: number
    favorites_count: number
  }
  isLoading: boolean
  isLoadingCommunications: boolean
  filters: {
    search: string
    temperature: string
    assigned_to: number | null
    pipeline_stage: number | null
    source: string
    include_closed: boolean
    contact_uuid: string | null
    historicalOnly?: boolean
  }
  pagination: {
    page: number
    pageSize: number
    totalPages: number
    totalCount: number
  }
  closureReasons: ClosureReason[]
  isLoadingClosureReasons: boolean
}

export const useLeadsStore = defineStore('leads', {
  state: (): LeadsState => ({
    leads: [],
    selectedLead: null,
    communications: [],
    pipelineStages: [],
    companyUsers: [],
    temperatureChoices: [
      ['hot', 'Quente'],
      ['warm', 'Morno'],
      ['cold', 'Frio'],
    ],
    leadCounts: {
      pending_count: 0,
      scheduled_count: 0,
      favorites_count: 0,
    },
    isLoading: false,
    isLoadingCommunications: false,
    filters: {
      search: '',
      temperature: '',
      assigned_to: null,
      pipeline_stage: null,
      source: '',
      include_closed: false,
      contact_uuid: null,
      historicalOnly: false,
    },
    pagination: {
      page: 1,
      pageSize: 20,
      totalPages: 0,
      totalCount: 0,
    },
    closureReasons: [],
    isLoadingClosureReasons: false,
  }),

  getters: {
    filteredLeads: (state) => {
      return state.leads.filter((lead) => {
        let matches = true

        if (state.filters.search) {
          const search = state.filters.search.toLowerCase()
          matches =
            matches &&
            (lead.name.toLowerCase().includes(search) ||
              lead.email.toLowerCase().includes(search) ||
              lead.phone.includes(search))
        }

        if (state.filters.temperature) {
          matches = matches && lead.temperature === state.filters.temperature
        }

        if (state.filters.assigned_to) {
          matches = matches && lead.assigned_to === state.filters.assigned_to
        }

        if (state.filters.pipeline_stage) {
          matches = matches && lead.pipeline_stage === state.filters.pipeline_stage
        }

        if (state.filters.source) {
          matches = matches && lead.source.toLowerCase().includes(state.filters.source.toLowerCase())
        }

        return matches
      })
    },

    selectedLeadCommunications: (state) => {
      return [...state.communications].sort(
        (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      )
    },

    getLeadById: (state) => {
      return (id: number) => state.leads.find((lead) => lead.id === id)
    },

    getStageById: (state) => {
      return (id: number) => state.pipelineStages.find((stage) => stage.id === id)
    },

    getUserById: (state) => {
      return (id: number) => state.companyUsers.find((user) => user.id === id)
    },
  },

  actions: {
    async fetchLeads(resetPage = false) {
      if (resetPage) {
        this.pagination.page = 1
      }

      this.isLoading = true

      try {
        const params = new URLSearchParams()

        params.append('page', this.pagination.page.toString())
        params.append('page_size', this.pagination.pageSize.toString())

        if (this.filters.search) {
          params.append('search', this.filters.search)
        }

        // Modo históricos: usa endpoint dedicado
        if (this.filters.historicalOnly) {
          const response = await CrmService.getHistoricalLeads(params.toString())
          this.leads = response.data.results || []
          this.pagination.totalPages = response.data.total_pages || 0
          this.pagination.totalCount = response.data.count || 0
          return
        }

        if (this.filters.temperature) {
          params.append('temperature', this.filters.temperature)
        }

        if (this.filters.assigned_to) {
          params.append('assigned_to', this.filters.assigned_to.toString())
        }

        if (this.filters.pipeline_stage) {
          params.append('pipeline_stage', this.filters.pipeline_stage.toString())
        }

        if (this.filters.source) {
          params.append('source', this.filters.source)
        }

        if (this.filters.include_closed) {
          params.append('include_closed', '1')
        }

        if (this.filters.contact_uuid) {
          params.append('contact', this.filters.contact_uuid)
        }

        // Aplicar filtros de permissão (não-admin vê apenas seus leads)
        const isAdmin = localStorage.getItem('cmpUrole') === 'admin'
        const currentUserId = localStorage.getItem('cmpU')

        if (!isAdmin && currentUserId) {
          params.append('assigned_to', currentUserId)
        }

        const response = await CrmService.getLeads(`?${params.toString()}`)

        this.leads = response.data.results || []
        this.pagination.totalPages = response.data.total_pages || 0
        this.pagination.totalCount = response.data.count || 0
      } catch (error) {
        devError('Error fetching leads:', error)
        throw error
      } finally {
        this.isLoading = false
      }
    },

    async fetchLeadManagement(leadId?: number) {
      this.isLoading = true

      try {
        const params: any = {}
        if (leadId) {
          params.lead_id = leadId
        }

        const response = await CrmService.getLeadManagement(params)

        // Atualizar contagens
        this.leadCounts = response.data.lead_counts || this.leadCounts

        // Atualizar lista de leads
        if (response.data.leads) {
          this.leads = response.data.leads
        }

        // Se há lead selecionado nos dados, atualizar
        if (response.data.selected_lead) {
          this.selectedLead = response.data.selected_lead
        }

        // Atualizar comunicações se houver
        if (response.data.communications) {
          this.communications = response.data.communications
        }

        // Atualizar dados auxiliares
        if (response.data.pipeline_stages) {
          this.pipelineStages = response.data.pipeline_stages
        }

        if (response.data.company_users) {
          this.companyUsers = response.data.company_users
        }

        if (response.data.temperature_choices) {
          this.temperatureChoices = response.data.temperature_choices
        }
      } catch (error) {
        devError('Error fetching lead management data:', error)
        throw error
      } finally {
        this.isLoading = false
      }
    },

    // Filtros auxiliares
    setHistoricalFilter(value: boolean) {
      this.filters.historicalOnly = value
    },

    async selectLead(lead: Lead) {
      this.selectedLead = lead
      await this.fetchCommunications(lead.id)
    },

    async fetchCommunications(leadId: number) {
      this.isLoadingCommunications = true

      try {
        const response = await CrmService.getCommunicationsByLead(leadId.toString())
        const rawList = Array.isArray(response.data) ? response.data : []

        // Normalização: mesclar confirmações de reação como reações da mensagem alvo
        const byId = new Map<number | string, any>()
        rawList.forEach((c: any) => {
          const key = (c && (c.id ?? c._id)) as any
          if (key !== undefined && key !== null) byId.set(String(key), { ...c })
        })

        const accumulateReaction = (target: any, reaction: string, actorId?: any) => {
          if (!reaction || typeof reaction !== 'string') return
          if (!target.reactions || typeof target.reactions !== 'object') {
            target.reactions = {}
          }
          if (!target.reactions[reaction]) {
            target.reactions[reaction] = []
          }
          // Evitar duplicados simples
          const actor = String(actorId ?? '')
          if (actor && Array.isArray(target.reactions[reaction])) {
            if (!target.reactions[reaction].includes(actor)) {
              target.reactions[reaction].push(actor)
            }
          }
        }

        const result: any[] = []

        for (const item of rawList) {
          const isReactionOnly =
            typeof item?.reaction === 'string' && (!item?.content || String(item.content).trim() === '')
          if (isReactionOnly) {
            const targetKey = item?.communication ?? item?.reply_to
            if (targetKey !== undefined && targetKey !== null) {
              const found = byId.get(String(targetKey))
              if (found) {
                const actorId = item?.user ?? item?.user_id ?? item?.user_details?.id
                accumulateReaction(found, item.reaction, actorId)
                // Não incluir a confirmação como mensagem própria
                continue
              }
            }
          }
          result.push(item)
        }

        this.communications = result
      } catch (error) {
        devError('Error fetching communications:', error)
        throw error
      } finally {
        this.isLoadingCommunications = false
      }
    },

    async sendMessage(payload: any, legacyContent?: string, legacyChannel: string = 'whatsapp message') {
      try {
        let messagePayload: any

        if (payload && typeof payload === 'object' && 'lead' in payload) {
          messagePayload = { ...payload }
        } else {
          const leadId = Number(payload)
          const currentUserId = localStorage.getItem('cmpU')
          messagePayload = {
            lead: leadId,
            user: currentUserId,
            channel: legacyChannel,
            content: legacyContent,
            direction: 'outgoing',
          }
        }

        const response = await CrmService.createCommunication(messagePayload)

        if (messagePayload.lead) {
          await this.fetchCommunications(Number(messagePayload.lead))
        }

        return response
      } catch (error) {
        devError('Error sending message:', error)
        throw error
      }
    },

    isClosedStage(stage?: PipelineStage | null) {
      if (!stage) return false
      if (typeof stage.is_closed === 'boolean') {
        return stage.is_closed
      }
      if (typeof stage.name === 'string') {
        const normalized = normalizeText(stage.name)
        return normalized.includes('fechado') || normalized.includes('closed')
      }
      return false
    },

    getClosedStage(): PipelineStage | null {
      return this.pipelineStages.find((stage) => this.isClosedStage(stage)) || null
    },

    async fetchClosureReasons(force = false) {
      if (!force && this.closureReasons.length > 0) {
        return this.closureReasons
      }

      this.isLoadingClosureReasons = true

      try {
        const response = await CrmService.getLeadClosureReasons()
        const payload = response?.data
        const results = Array.isArray(payload?.results) ? payload.results : payload

        this.closureReasons = Array.isArray(results) ? results : []
        devLog(`✅ Motivos de fechamento carregados: ${this.closureReasons.length} disponíveis`)
        return this.closureReasons
      } catch (error) {
        devError('❌ Erro ao buscar motivos de fechamento:', error)
        this.closureReasons = []
        throw error
      } finally {
        this.isLoadingClosureReasons = false
      }
    },

    applyLeadUpdate(
      leadId: number,
      stageId: number | null,
      options: {
        serverLead?: any
        closureReasonId?: number
        closureNotes?: string | null
        isClosedStage: boolean
      },
    ) {
      const { serverLead, closureReasonId, closureNotes, isClosedStage } = options
      const stage = stageId != null ? this.getStageById(stageId) : (serverLead?.current_stage ?? null)
      const closureReason =
        closureReasonId != null
          ? this.closureReasons.find((reason) => reason.id === closureReasonId) || null
          : (serverLead?.closure_reason ?? null)

      const normalizeLead = (lead: Lead | null) => {
        if (!lead) return lead

        const base =
          serverLead && typeof serverLead === 'object' && Object.keys(serverLead).length > 0
            ? { ...lead, ...serverLead }
            : { ...lead }

        if (stageId != null) {
          base.pipeline_stage = stageId
        } else if (serverLead?.pipeline_stage != null) {
          base.pipeline_stage = serverLead.pipeline_stage
        }

        if (stage) {
          base.current_stage = stage as any
        }

        if (isClosedStage) {
          if (closureReason) {
            base.closure_reason = closureReason
            base.closure_reason_id =
              closureReason?.id ?? serverLead?.closure_reason_id ?? base.closure_reason_id ?? null
          } else if (serverLead?.closure_reason_id !== undefined) {
            base.closure_reason_id = serverLead.closure_reason_id
          }

          if (closureNotes !== undefined) {
            base.closure_notes = closureNotes ?? null
          } else if (serverLead?.closure_notes !== undefined) {
            base.closure_notes = serverLead.closure_notes
          }

          base.status = serverLead?.status ?? base.status ?? 'closed'
          if (serverLead?.closed_at !== undefined) {
            base.closed_at = serverLead.closed_at
          }
        } else {
          if (serverLead?.status !== undefined) {
            base.status = serverLead.status
          } else if (base.status === 'closed') {
            base.status = 'open'
          }

          if (serverLead?.closed_at !== undefined) {
            base.closed_at = serverLead.closed_at
          } else if (serverLead?.closed_at === undefined) {
            base.closed_at = null
          }

          if (serverLead?.closure_reason !== undefined) {
            base.closure_reason = serverLead.closure_reason
            base.closure_reason_id = serverLead.closure_reason?.id ?? serverLead.closure_reason_id ?? null
          } else {
            base.closure_reason = null
            base.closure_reason_id = null
          }

          if (serverLead?.closure_notes !== undefined) {
            base.closure_notes = serverLead.closure_notes
          } else {
            base.closure_notes = null
          }
        }

        if (serverLead?.closed_by !== undefined) {
          base.closed_by = serverLead.closed_by
        }

        if (serverLead?.closed_by_details !== undefined) {
          base.closed_by_details = serverLead.closed_by_details
        }

        return base
      }

      const idx = this.leads.findIndex((lead) => lead.id === leadId)
      if (idx !== -1) {
        this.leads[idx] = normalizeLead(this.leads[idx]) as Lead
      }

      if (this.selectedLead && this.selectedLead.id === leadId) {
        this.selectedLead = normalizeLead(this.selectedLead) as Lead
      }
    },

    async closeLead(leadId: number, closureReasonId: number, closureNotes?: string | null) {
      try {
        await this.fetchClosureReasons(false)

        const payload: {
          closure_reason_id: number
          closure_notes?: string
        } = {
          closure_reason_id: closureReasonId,
        }

        if (closureNotes && closureNotes.trim()) {
          payload.closure_notes = closureNotes.trim()
        }

        const response = await CrmService.closeLead(leadId.toString(), payload)

        const closedStage = this.getClosedStage()
        const stageId = closedStage?.id ?? response?.data?.pipeline_stage ?? null

        this.applyLeadUpdate(leadId, stageId, {
          serverLead: response?.data,
          closureReasonId,
          closureNotes: closureNotes ?? null,
          isClosedStage: true,
        })

        return response
      } catch (error) {
        devError('Error closing lead:', error)
        throw error
      }
    },

    async updateLeadStage(
      leadId: number,
      stageId: number,
      options: { closureReasonId?: number; closureNotes?: string | null } = {},
    ) {
      try {
        const targetStage = this.getStageById(stageId)
        const isClosedStage = this.isClosedStage(targetStage)

        if (isClosedStage) {
          await this.fetchClosureReasons(false)
          if (!options.closureReasonId) {
            throw new Error('closureReasonId is required when moving a lead to a closed stage.')
          }
        }

        const payload: {
          stage_id: number
          closure_reason_id?: number | null
          closure_notes?: string | null
        } = {
          stage_id: stageId,
        }

        if (isClosedStage) {
          payload.closure_reason_id = options.closureReasonId!
          if (options.closureNotes && options.closureNotes.trim()) {
            payload.closure_notes = options.closureNotes.trim()
          }
        } else {
          payload.closure_reason_id = null
          payload.closure_notes = null
        }

        const response = await CrmService.updateLeadStage(leadId.toString(), payload)

        this.applyLeadUpdate(leadId, stageId, {
          serverLead: response?.data,
          closureReasonId: isClosedStage ? options.closureReasonId : undefined,
          closureNotes: isClosedStage ? (options.closureNotes ?? null) : null,
          isClosedStage,
        })

        return response
      } catch (error) {
        devError('Error updating lead stage:', error)
        throw error
      }
    },

    async assignLead(leadId: number, userId: number) {
      try {
        const response = await CrmService.assignLead(leadId.toString(), userId)

        // Atualizar o lead na lista local
        const leadIndex = this.leads.findIndex((lead) => lead.id === leadId)
        if (leadIndex !== -1) {
          this.leads[leadIndex] = { ...this.leads[leadIndex], assigned_to: userId }
        }

        // Atualizar lead selecionado se necessário
        if (this.selectedLead && this.selectedLead.id === leadId) {
          this.selectedLead = { ...this.selectedLead, assigned_to: userId }
        }

        return response
      } catch (error) {
        devError('Error assigning lead:', error)
        throw error
      }
    },

    async fetchPipelineStages() {
      try {
        const response = await CrmService.getPipelineStages()
        this.pipelineStages = response.data || []
      } catch (error) {
        devError('Error fetching pipeline stages:', error)
        throw error
      }
    },

    async fetchCompanyUsers() {
      try {
        const response = await CrmService.getCompanyUsers()
        this.companyUsers = response.data || []
      } catch (error) {
        devError('Error fetching company users:', error)
        throw error
      }
    },

    // Ações para filtros
    setSearchFilter(search: string) {
      this.filters.search = search
    },

    setTemperatureFilter(temperature: string) {
      this.filters.temperature = temperature
    },

    setAssignedToFilter(userId: number | null) {
      this.filters.assigned_to = userId
    },

    setPipelineStageFilter(stageId: number | null) {
      this.filters.pipeline_stage = stageId
    },

    setSourceFilter(source: string) {
      this.filters.source = source
    },

    setIncludeClosedFilter(includeClosed: boolean) {
      this.filters.include_closed = includeClosed
    },

    setContactFilter(contactUuid: string | null) {
      this.filters.contact_uuid = contactUuid
    },

    clearFilters() {
      this.filters = {
        search: '',
        temperature: '',
        assigned_to: null,
        pipeline_stage: null,
        source: '',
        include_closed: false,
        contact_uuid: null,
      }
    },

    // Ações para paginação
    setPage(page: number) {
      this.pagination.page = page
    },

    setPageSize(pageSize: number) {
      this.pagination.pageSize = pageSize
      this.pagination.page = 1 // Reset to first page
    },

    // Limpar seleção de lead e comunicações associadas
    clearSelectedLead() {
      this.selectedLead = null
      this.communications = []
    },

    async fetchLeadsByContact(contactUuid: string, params: { page?: number; page_size?: number } = {}) {
      this.isLoading = true

      try {
        const contactsStore = useContactsStore()
        const response = await contactsStore.getLeadsByContact(contactUuid, params)

        const results = response?.results || response || []

        this.leads = Array.isArray(results) ? results : []
        this.filters.contact_uuid = contactUuid

        const total = response?.count
        if (typeof total === 'number') {
          this.pagination.totalCount = total
          this.pagination.totalPages = Math.ceil(total / this.pagination.pageSize)
        } else {
          this.pagination.totalCount = this.leads.length
          this.pagination.totalPages = 1
        }

        this.pagination.page = params.page ?? 1

        return this.leads
      } catch (error) {
        devError('Error fetching leads by contact:', error)
        throw error
      } finally {
        this.isLoading = false
      }
    },

    clearData() {
      devLog('🧹 Clearing leadsStore data')
      this.clearFilters()
      this.clearSelectedLead()
      this.leads = []
      this.closureReasons = []
    },
  },
})
